import { useState, useEffect, useCallback, useRef } from 'react';
import * as Popover from '@radix-ui/react-popover';
import contentCalendarService from '@/services/contentCalendarService';
import apiService from '@/services/api';
import type { ContentCalendar, ContentPost, WeeklyTheme, PostStatus } from '@/types/contentCalendar';

// ─── Platform display config ────────────────────────────────────────────────

const platformColors: Record<string, string> = {
  LinkedIn: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Instagram: 'bg-amber-50 text-amber-700 border-amber-200',
  'Twitter / X': 'bg-stone-100 text-stone-700 border-stone-200',
  Twitter: 'bg-stone-100 text-stone-700 border-stone-200',
  Facebook: 'bg-teal-50 text-teal-700 border-teal-200',
  YouTube: 'bg-rose-50 text-rose-700 border-rose-200',
};

const platformIcons: Record<string, string> = {
  LinkedIn: 'ri-linkedin-box-line',
  Instagram: 'ri-instagram-line',
  'Twitter / X': 'ri-twitter-line',
  Twitter: 'ri-twitter-line',
  Facebook: 'ri-facebook-circle-line',
  YouTube: 'ri-youtube-line',
};

// Visual-only left accent stripe via box-shadow (does NOT affect the box model / grid width)
const platformAccentShadow: Record<string, string> = {
  LinkedIn: 'inset 4px 0 0 #10b981',   // emerald-500
  Instagram: 'inset 4px 0 0 #f59e0b',  // amber-500
  'Twitter / X': 'inset 4px 0 0 #78716c', // stone-500
  Twitter: 'inset 4px 0 0 #78716c',
  Facebook: 'inset 4px 0 0 #14b8a6',   // teal-500
  YouTube: 'inset 4px 0 0 #f43f5e',    // rose-500
};

const formatIcons: Record<string, string> = {
  Article: 'ri-article-line',
  Carousel: 'ri-stack-line',
  Thread: 'ri-list-check',
  Tutorial: 'ri-video-line',
  Reel: 'ri-movie-line',
  Poll: 'ri-survey-line',
  'Single Tweet': 'ri-chat-1-line',
  'Story Series': 'ri-stack-line',
  Event: 'ri-calendar-event-line',
  Document: 'ri-file-list-line',
  Interview: 'ri-user-voice-line',
  Video: 'ri-video-line',
  Short: 'ri-video-line',
  'Static Image': 'ri-image-line',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In Progress',
  DONE: 'Completed',
  NEEDS_REVISION: 'Needs Revision',
  NOT_STARTED: 'Not Started',
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-600',
  SCHEDULED: 'bg-blue-50 text-blue-700',
  IN_PROGRESS: 'bg-amber-50 text-amber-700',
  DONE: 'bg-emerald-100 text-emerald-700',
  NEEDS_REVISION: 'bg-rose-50 text-rose-700',
  NOT_STARTED: 'bg-gray-100 text-gray-600',
};

// ─── Column widths (must sum to 100%) ────────────────────────────────────────
// POST/CHANNEL 12% | THEME 10% | FORMAT 14% | CREATIVE COPY 20% |
// DATE 11% | CAPTION/HASHTAGS 21% | STATUS 12%
const COL_WIDTHS = '12% 10% 14% 20% 11% 21% 12%';

// ─── Helpers ────────────────────────────────────────────────────────────────

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Returns a human-readable date range for a set of posts, e.g. "1 Sep – 30 Sep 2026".
 * Falls back to a month-level label ("September 2026") when no post has a scheduledDate.
 */
function calendarDateRange(posts: ContentPost[], fallbackMonth?: string): string {
  const dates = posts
    .map((p) => p.scheduledDate)
    .filter(Boolean)
    .map((iso) => new Date(`${(iso as string).slice(0, 10)}T00:00:00`).getTime())
    .filter((t) => !isNaN(t));

  if (dates.length > 0) {
    const minD = new Date(Math.min(...dates));
    const maxD = new Date(Math.max(...dates));
    const fmt = (d: Date) =>
      d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const year = maxD.getFullYear();
    return `${fmt(minD)} ${year} to ${fmt(maxD)} ${year}`;
  }

  // Fallback: derive from the "YYYY-MM" month string
  if (fallbackMonth) {
    const m = /^(\d{4})-(\d{2})$/.exec(fallbackMonth);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    }
  }

  return new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

function groupByWeek(posts: ContentPost[]): Record<number, ContentPost[]> {
  return posts.reduce<Record<number, ContentPost[]>>((acc, p) => {
    const w = p.week ?? 1;
    if (!acc[w]) acc[w] = [];
    acc[w].push(p);
    return acc;
  }, {});
}

// ─── Loading Skeleton ────────────────────────────────────────────────────────

function Skeleton({ className = '' }: { className?: string }) {
  return (
    <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
  );
}

// ─── HoverPopover ────────────────────────────────────────────────────────────
// A lightweight hover-triggered popover that shows full content.
// Opens after 150 ms hover delay, closes on mouse-leave or outside click.

type PopoverSide = 'top' | 'right' | 'bottom' | 'left';
type PopoverAlign = 'start' | 'center' | 'end';

interface HoverPopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  /** Which side the panel appears on. Default: 'bottom' */
  side?: PopoverSide;
  /** Alignment along the anchor axis. Default: 'start' */
  align?: PopoverAlign;
  /** Extra classes applied to the Popover.Content panel */
  contentClassName?: string;
  /** Delay before opening in ms. Default: 150 */
  openDelayMs?: number;
}

function HoverPopover({
  trigger,
  children,
  side = 'bottom',
  align = 'start',
  contentClassName = '',
  openDelayMs = 150,
}: HoverPopoverProps) {
  const [open, setOpen] = useState(false);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule the panel to close after a grace period (so the mouse can travel
  // from the trigger into the content without the panel snapping shut).
  const scheduleClose = (delayMs = 200) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpen(false), delayMs);
  };

  // Cancel any pending close (called when the mouse re-enters trigger or content).
  const cancelClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  const handleTriggerEnter = () => {
    cancelClose();
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => setOpen(true), openDelayMs);
  };

  const handleTriggerLeave = () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    // Give the user 200 ms to move the mouse into the content before closing.
    scheduleClose(200);
  };

  const handleContentEnter = () => {
    // Mouse is now inside the panel — cancel any pending close so the user
    // can read / scroll without the panel disappearing.
    cancelClose();
  };

  const handleContentLeave = () => {
    // Mouse left the content panel; close after a short delay.
    scheduleClose(120);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <div
          onMouseEnter={handleTriggerEnter}
          onMouseLeave={handleTriggerLeave}
          onClick={() => setOpen((v) => !v)}
          className="w-full cursor-pointer"
        >
          {trigger}
        </div>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align={align}
          sideOffset={8}
          onMouseEnter={handleContentEnter}
          onMouseLeave={handleContentLeave}
          className={`z-50 rounded-xl border border-gray-200 bg-white shadow-lg text-sm text-gray-700 leading-relaxed ${contentClassName}`}
        >
          {children}
          <Popover.Arrow className="fill-white stroke-gray-200" strokeWidth={1} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ─── CreativeCopyTooltip ──────────────────────────────────────────────────────
// Side-anchored horizontal tooltip for the Creative Copy column.
// Appears to the right of the cell (falls back to left automatically via Radix
// collision detection) so it never covers sibling rows.

function CreativeCopyTooltip({ copy }: { copy: string }) {
  if (!copy) return <p className="text-sm text-gray-400 italic">—</p>;

  // Collapse 2-line preview in cell
  const trigger = (
    <p className="text-sm text-gray-700 leading-relaxed line-clamp-2 group-hover:text-gray-900 transition-colors">
      {copy}
    </p>
  );

  return (
    <HoverPopover
      trigger={trigger}
      side="right"
      align="center"
      openDelayMs={120}
      contentClassName="w-96 max-w-[min(384px,60vw)] p-0 overflow-hidden"
    >
      {/* Header bar */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-gray-50 rounded-t-xl">
        <i className="ri-quill-pen-line text-blue-500 text-sm" />
        <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Creative Copy</span>
      </div>
      {/* Scrollable body — capped so very long scripts don't overflow the viewport */}
      <div className="px-4 py-3 max-h-60 overflow-y-auto">
        <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{copy}</p>
      </div>
    </HoverPopover>
  );
}


// ─── HashtagBadge ─────────────────────────────────────────────────────────────
// Shows "+N tags" badge when there are hashtags

function HashtagCountBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[10px] font-semibold border border-blue-100 flex-shrink-0">
      +{count} tags
    </span>
  );
}

// ─── Hashtag Chip ─────────────────────────────────────────────────────────────

function HashtagChip({ tag }: { tag: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium border border-blue-100">
      #{tag}
    </span>
  );
}

// ─── CalendarRow (desktop fixed-layout) ──────────────────────────────────────

interface CalendarRowProps {
  post: ContentPost;
  idx: number;
  isUpdating: boolean;
  isDone: boolean;
  canMarkDone: boolean;
  onToggle: () => void;
}

function CalendarRow({ post, idx, isUpdating, isDone, canMarkDone, onToggle }: CalendarRowProps) {
  const accentShadow = platformAccentShadow[post.channel] ?? 'inset 4px 0 0 #d1d5db';
  const hashtags: string[] = post.hashtags ?? [];
  const creativeCopy = post.creativeCopy || post.hook || '';

  return (
    <div
      className={`cpe-row border border-gray-200 rounded-xl transition-colors ${isDone ? 'bg-emerald-50/60' : 'bg-white hover:bg-gray-50/60'
        }`}
      style={{ boxShadow: accentShadow }}
    >
      {/* ── POST / CHANNEL ─────────────────────────────────────── */}
      <div className="cpe-cell border-r border-gray-100">
        <div className="flex flex-col gap-1.5">
          <span className="w-fit px-1.5 py-0.5 bg-gray-900 text-white text-[10px] font-bold rounded">
            Post {idx + 1}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium border w-fit ${platformColors[post.channel] ?? 'bg-gray-100 text-gray-700 border-gray-200'
              }`}
          >
            <i className={`${platformIcons[post.channel] ?? 'ri-global-line'} text-xs`} />
            {post.channel}
          </span>
        </div>
      </div>

      {/* ── THEME ──────────────────────────────────────────────── */}
      <div className="cpe-cell border-r border-gray-100">
        <HoverPopover
          trigger={
            <p className="text-sm font-semibold text-gray-800 line-clamp-2">
              {post.themeType}
            </p>
          }
        >
          <p className="font-semibold text-gray-900 mb-1 text-xs uppercase tracking-wider text-gray-400">Theme</p>
          <p className="text-sm text-gray-800 font-semibold">{post.themeType}</p>
        </HoverPopover>
      </div>

      {/* ── CONTENT FORMAT ─────────────────────────────────────── */}
      <div className="cpe-cell border-r border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center rounded bg-gray-100 flex-shrink-0">
            <i className={`${formatIcons[post.contentFormat] ?? 'ri-file-text-line'} text-sm text-gray-500`} />
          </div>
          <span className="text-sm font-medium text-gray-700 min-w-0 break-words">{post.contentFormat}</span>
        </div>
      </div>

      {/* ── CREATIVE COPY ──────────────────────────────────────── */}
      <div className="cpe-cell border-r border-gray-100 group">
        <CreativeCopyTooltip copy={creativeCopy} />
      </div>

      {/* ── DATE ───────────────────────────────────────────────── */}
      <div className="cpe-cell border-r border-gray-100">
        <div className="flex items-center gap-3 bg-gray-50 rounded-lg border border-gray-100 px-3 py-2 w-full">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-800">
              {formatDate(post.scheduledDate)}
            </span>
            <span className="text-[11px] text-gray-400 flex items-center gap-1">
              <i className="ri-time-line text-[10px]" />
              Scheduled
            </span>
          </div>
        </div>
      </div>

      {/* ── CAPTION / HASHTAGS ─────────────────────────────────── */}
      <div className="cpe-cell border-r border-gray-100">
        <HoverPopover
          trigger={
            <div className="flex flex-col gap-1.5 w-full">
              <p className="text-sm text-gray-700 leading-relaxed line-clamp-1">
                {post.caption}
              </p>
              <HashtagCountBadge count={hashtags.length} />
            </div>
          }
          contentClassName="w-80 max-w-[min(320px,88vw)] p-0 overflow-hidden"
        >
          {/* Header bar */}
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-gray-50 rounded-t-xl">
            <i className="ri-chat-quote-line text-blue-500 text-sm" />
            <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">Caption &amp; Hashtags</span>
          </div>
          {/* Scrollable body */}
          <div className="px-4 py-3 max-h-56 overflow-y-auto space-y-3">
            <p className="text-sm text-gray-800 leading-relaxed">{post.caption}</p>
            {hashtags.length > 0 && (
              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Hashtags</p>
                <div className="flex flex-wrap gap-1.5">
                  {hashtags.map((tag) => (
                    <HashtagChip key={tag} tag={tag} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </HoverPopover>
      </div>

      {/* ── STATUS / MARK AS COMPLETE ──────────────────────────── */}
      <div className="cpe-cell">
        <button
          disabled={isUpdating || (!canMarkDone && !isDone)}
          onClick={onToggle}
          title={!canMarkDone && !isDone ? 'Generate media before marking as Completed' : undefined}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${isDone
            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            : !canMarkDone
              ? 'bg-gray-100 text-gray-400'
              : (STATUS_COLORS[post.status] || 'bg-gray-100 text-gray-600') + ' hover:opacity-80'
            }`}
        >
          {isUpdating ? (
            <i className="ri-loader-4-line animate-spin text-sm" />
          ) : (
            <i
              className={`${isDone ? 'ri-checkbox-circle-fill' : !canMarkDone ? 'ri-lock-line' : 'ri-checkbox-blank-circle-line'} text-sm`}
            />
          )}
          {isDone ? 'Completed' : !canMarkDone ? 'No Media' : STATUS_LABELS[post.status] ?? post.status}
        </button>
      </div>
    </div>
  );
}

// ─── Mobile Card Row ─────────────────────────────────────────────────────────

interface MobileCardRowProps {
  post: ContentPost;
  idx: number;
  isUpdating: boolean;
  isDone: boolean;
  canMarkDone: boolean;
  onToggle: () => void;
}

function MobileCardRow({ post, idx, isUpdating, isDone, canMarkDone, onToggle }: MobileCardRowProps) {
  const [expanded, setExpanded] = useState(false);
  const accentShadow = platformAccentShadow[post.channel] ?? 'inset 4px 0 0 #d1d5db';
  const hashtags: string[] = post.hashtags ?? [];
  const creativeCopy = post.creativeCopy || post.hook || '';

  return (
    <div
      className={`rounded-xl border border-gray-200 transition-colors ${isDone ? 'bg-emerald-50/60' : 'bg-white'
        }`}
      style={{ boxShadow: accentShadow }}
    >
      {/* Header row */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <div className="flex items-center gap-2">
          <span className="px-1.5 py-0.5 bg-gray-900 text-white text-[10px] font-bold rounded">
            Post {idx + 1}
          </span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${platformColors[post.channel] ?? 'bg-gray-100 text-gray-700 border-gray-200'
              }`}
          >
            <i className={`${platformIcons[post.channel] ?? 'ri-global-line'} text-xs`} />
            {post.channel}
          </span>
        </div>
        <button
          disabled={isUpdating || (!canMarkDone && !isDone)}
          onClick={onToggle}
          title={!canMarkDone && !isDone ? 'Generate media before marking as Completed' : undefined}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed ${isDone
            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
            : !canMarkDone
              ? 'bg-gray-100 text-gray-400'
              : (STATUS_COLORS[post.status] || 'bg-gray-100 text-gray-600') + ' hover:opacity-80'
            }`}
        >
          {isUpdating ? (
            <i className="ri-loader-4-line animate-spin text-sm" />
          ) : (
            <i className={`${isDone ? 'ri-checkbox-circle-fill' : !canMarkDone ? 'ri-lock-line' : 'ri-checkbox-blank-circle-line'} text-sm`} />
          )}
          {isDone ? 'Completed' : !canMarkDone ? 'No Media' : STATUS_LABELS[post.status] ?? post.status}
        </button>
      </div>

      {/* Summary row — Theme + Format + Date inline */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 pb-2">
        <span className="text-xs font-semibold text-gray-700">{post.themeType}</span>
        <span className="text-gray-300">·</span>
        <span className="inline-flex items-center gap-1 text-xs text-gray-500">
          <i className={`${formatIcons[post.contentFormat] ?? 'ri-file-text-line'} text-[11px]`} />
          {post.contentFormat}
        </span>
        <span className="text-gray-300">·</span>
        <span className="text-xs text-gray-500">{formatDate(post.scheduledDate)}</span>
      </div>

      {/* Caption preview */}
      <div className="px-4 pb-2">
        <p className={`text-sm text-gray-700 leading-relaxed ${expanded ? '' : 'line-clamp-2'}`}>
          {post.caption}
        </p>
      </div>

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-center gap-1 py-2 text-xs text-blue-600 font-medium border-t border-gray-100 hover:bg-gray-50 transition-colors rounded-b-xl"
      >
        {expanded ? (
          <>Less <i className="ri-arrow-up-s-line" /></>
        ) : (
          <>More &amp; hashtags <i className="ri-arrow-down-s-line" /></>
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-3 pt-2 border-t border-gray-100 space-y-3">
          {creativeCopy && (
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">Creative Copy</p>
              <p className="text-sm text-gray-700 leading-relaxed">{creativeCopy}</p>
            </div>
          )}
          {hashtags.length > 0 && (
            <div>
              <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Hashtags</p>
              <div className="flex flex-wrap gap-1.5">
                {hashtags.map((tag) => (
                  <HashtagChip key={tag} tag={tag} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ContentPlanningEngine({ setActiveSection }: { setActiveSection?: (section: string) => void }) {
  const [selectedWeek, setSelectedWeek] = useState<1 | 2 | 3 | 4>(1);
  const [calendar, setCalendar] = useState<ContentCalendar | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingPostId, setUpdatingPostId] = useState<string | null>(null);

  // ── Derive display data from loaded calendar ──────────────────────────────

  const weeklyThemes: WeeklyTheme[] = calendar?.weeklyThemes ?? [];
  const postsByWeek = groupByWeek(calendar?.posts ?? []);
  const postsForWeek: ContentPost[] = postsByWeek[selectedWeek] ?? [];

  // ── Load on mount ─────────────────────────────────────────────────────────

  const loadCalendar = useCallback(async () => {
    const userId = localStorage.getItem('user_id');
    if (!userId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      let cal = await contentCalendarService.fetchOrNull(userId, currentMonth());

      if (!cal) {
        try {
          cal = await contentCalendarService.generateCalendar(userId, currentMonth(), false);
        } catch (genErr: any) {
          console.error('Auto-generation failed:', genErr);
          // Show the reason (expired subscription, AI unavailable, setup
          // missing…) instead of falling through to "No content calendar yet".
          setError(genErr?.message || 'Could not generate your content calendar.');
        }
      }

      setCalendar(cal);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load content calendar');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  // ── Mark post as Done / toggle back ───────────────────────────────────────

  const [statusError, setStatusError] = useState<string | null>(null);

  const handleToggleStatus = async (post: ContentPost) => {
    if (updatingPostId) return;

    const nextStatus: PostStatus = post.status === 'DONE' ? 'DRAFT' : 'DONE';
    setUpdatingPostId(post.id);
    setStatusError(null);
    try {
      const updated = await contentCalendarService.updatePostStatus(post.id, nextStatus);
      setCalendar((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          posts: prev.posts.map((p) => (p.id === updated.id ? updated : p)),
        };
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : typeof detail === 'object' && detail?.message ? detail.message
          : err?.message || 'Failed to update status';
      setStatusError(msg);
      console.error('Failed to update post status:', msg);
    } finally {
      setUpdatingPostId(null);
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async () => {
    if (!calendar || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      // <brand_name>_<month>_content_calendar.csv instead of a fixed
      // "content_calendar.csv" — resolve the brand name from the business
      // profile (non-fatal if it fails) and the month from the calendar
      // itself (typically "YYYY-MM").
      const sanitizeForFilename = (s: string) =>
        s.trim().replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');

      let brandLabel = 'brand';
      const userId = localStorage.getItem('user_id');
      if (userId) {
        try {
          const response = await apiService.getFullBusinessProfile(userId);
          const bp = (response as any)?.data?.business_profile || {};
          const cleaned = sanitizeForFilename(bp.brand_name || bp.business_name || '');
          if (cleaned) brandLabel = cleaned;
        } catch (e) {
          console.warn('[ContentPlanningEngine] Failed to fetch business profile for export filename:', e);
        }
      }

      const rangeLabel = calendarDateRange(calendar.posts ?? [], calendar.month);
      const monthLabel = sanitizeForFilename(rangeLabel);

      const filename = monthLabel
        ? `${brandLabel}_${monthLabel}_content_calendar.csv`
        : `${brandLabel}_content_calendar.csv`;

      await contentCalendarService.downloadCalendar(calendar.id, 'CSV', filename);
    } catch (err: any) {
      console.error('Export failed:', err?.message);
      setExportError(err?.message || 'Could not export the calendar. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // ── Render: Loading state ─────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-6 w-full">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <Skeleton className="h-6 w-48 mb-5" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((w) => (
              <div key={w} className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-gray-50/50 rounded-xl border border-gray-200 p-4 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // ── Render: Error state ───────────────────────────────────────────────────

  if (error) {
    return (
      <div className="w-full bg-rose-50 border border-rose-200 rounded-xl p-6 flex items-start gap-3">
        <i className="ri-error-warning-line text-rose-600 text-xl flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-rose-800">Failed to load content calendar</p>
          <p className="text-xs text-rose-600 mt-1">{error}</p>
          <button
            onClick={loadCalendar}
            className="mt-3 px-3 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-800 rounded-lg text-xs font-semibold transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Render: Empty state ───────────────────────────────────────────────────

  if (!calendar) {
    return (
      <div className="w-full bg-white border border-gray-200 rounded-xl p-8 text-center space-y-4">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-100 mx-auto">
          <i className="ri-calendar-todo-line text-gray-500 text-2xl" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">No content calendar yet</h3>
          <p className="text-sm text-gray-500 mt-1">
            Refresh to generate content calendar
          </p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          <i className="ri-arrow-left-line text-xs" />
          Reload
        </button>
      </div>
    );
  }

  // ── Render: Calendar loaded ───────────────────────────────────────────────

  return (
    <>
      {/*
        Scoped styles for the fixed-layout table rows.
        Using a <style> tag keeps this self-contained without touching global CSS.
      */}
      <style>{`
        /*
         * ALIGNMENT CONTRACT
         * ─────────────────────────────────────────────────────────────────────
         * Both .cpe-header and .cpe-row are siblings inside the same .cpe-body
         * div which has padding: 12px (p-3). The header uses negative margins
         * (-mx-3, -mt-3) to stretch edge-to-edge, then re-applies pl-4 (16px)
         * and pr-3 (12px) so its grid-content area = containerW - 28px.
         * Rows have no border-l (accent is box-shadow inset — zero layout
         * impact), so their grid-content area is also containerW - 24px (p-3
         * each side) == actually the rows sit inside the same .cpe-body which
         * already accounts for the 12px sides.  Both use identical
         * grid-template-columns percentages resolved against identical widths.
         * Column dividers (border-r) therefore land at the same pixel position.
         */

        /* ── Desktop fixed-layout grid row ── */
        .cpe-row {
          display: none;
          min-height: 88px;
        }
        @media (min-width: 1024px) {
          .cpe-row {
            display: grid;
            grid-template-columns: ${COL_WIDTHS};
            align-items: stretch;
          }
        }
        /* Each cell stretches full height and vertically centers its content */
        .cpe-cell {
          display: flex;
          align-items: center;
          padding: 10px 8px;
          min-height: 88px;
          min-width: 0;
          overflow: hidden;
        }
        /* Header — same grid template, stretched to container edges via -mx-3 -mt-3 */
        .cpe-header {
          display: none;
        }
        @media (min-width: 1024px) {
          .cpe-header {
            display: grid;
            grid-template-columns: ${COL_WIDTHS};
          }
        }
        .cpe-header-cell {
          padding: 10px 8px;
          font-size: 10px;
          font-weight: 700;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          white-space: normal;
          word-break: break-word;
          overflow: visible;
          text-overflow: clip;
        }
      `}</style>

      <div className="space-y-6 w-full">

        {/* ── Monthly Theme Overview ───────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-900">
              <i className="ri-calendar-todo-line text-white text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Monthly Content Themes</h3>
              <p className="text-sm text-gray-500">Strategic pillars guiding all content for this month</p>
            </div>
          </div>

          {weeklyThemes.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {weeklyThemes.map((mt) => (
                <div key={mt.week} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-gray-900 text-white text-xs font-bold rounded">
                      W{mt.week}
                    </span>
                    <span className="text-xs font-medium text-gray-500">{mt.pillar}</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">{mt.themeLabel}</p>
                  <p className="text-xs text-gray-500">{mt.focus}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">
              Weekly themes will appear once the calendar is generated.
            </p>
          )}
        </div>

        {/* ── Week Selector + Calendar title ──────────────────────────────── */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Content Calendar</h3>
          <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
            {([1, 2, 3, 4] as const).map((w) => (
              <button
                key={w}
                onClick={() => setSelectedWeek(w)}
                className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${selectedWeek === w ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
              >
                Week {w}
              </button>
            ))}
          </div>
        </div>

        {/* ── Info Banner ─────────────────────────────────────────────────── */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 flex-shrink-0 mt-0.5">
            <i className="ri-lightbulb-line text-amber-700 text-sm" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">
              Recommended Social Media Calendar for{' '}
              {calendarDateRange(calendar?.posts ?? [], calendar?.month)}
            </p>
            <p className="text-xs text-amber-700 mt-1">
              You can edit posts in the Implementation Dashboard
            </p>
          </div>
          <button
            onClick={() => setActiveSection?.('content-calendar-workspace')}
            className="flex items-center gap-1.5 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap"
          >
            Go to Implementation Screen
            <i className="ri-arrow-right-line text-xs" />
          </button>
        </div>

        {/* ── Status Error Banner ────────────────────────────────────────── */}
        {statusError && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500 text-sm" />
              <p className="text-sm text-red-700">{statusError}</p>
            </div>
            <button onClick={() => setStatusError(null)} className="text-red-400 hover:text-red-600">
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        )}

        {/* ── Content Calendar Table ───────────────────────────────────────── */}
        <div className="bg-gray-50/50 rounded-xl border border-gray-200 overflow-x-auto">
          {/* min-width keeps the table scrollable on narrow viewports */}
          <div style={{ minWidth: '760px' }}>

            {/*
             * .cpe-body: the single p-3 coordinate space shared by BOTH the
             * header and all data rows. The header uses -mx-3 -mt-3 to bleed
             * to the container edges so it gets a full-width background while
             * still being laid out in the same grid context.
             */}
            <div className="cpe-body p-3 flex flex-col gap-3">

              {/* ── Desktop Table Header ────────────────────────── */}
              <div className="cpe-header -mx-3 -mt-3 bg-gray-50 border-b border-gray-200 rounded-t-xl">
                <div className="cpe-header-cell border-r border-gray-100">Post / Channel</div>
                <div className="cpe-header-cell border-r border-gray-100">Theme</div>
                <div className="cpe-header-cell border-r border-gray-100">Content Format</div>
                <div className="cpe-header-cell border-r border-gray-100">Creative Copy</div>
                <div className="cpe-header-cell border-r border-gray-100">Day & Date</div>
                <div className="cpe-header-cell border-r border-gray-100">Caption / Hashtags</div>
                <div className="cpe-header-cell">Mark as Complete</div>
              </div>

              {/* ── Rows ────────────────────────────────────────── */}
              {postsForWeek.length === 0 ? (
                <p className="text-sm text-gray-400 italic text-center py-8">
                  No posts scheduled for Week {selectedWeek}.
                </p>
              ) : (
                postsForWeek.map((post, idx) => {
                  const isDone = post.status === 'DONE';
                  const isUpdating = updatingPostId === post.id;
                  const fmt = (post.contentFormat || '').toLowerCase();
                  const hasMedia = !!(post as any).imageUrl || ((post as any).carouselSlideUrls?.length > 0);
                  const hasContent = !!(post as any).creativeCopy || !!(post as any).caption;
                  const canMarkDone = fmt === 'article' ? hasContent : hasMedia;

                  return (
                    <div key={post.id}>
                      {/* Desktop fixed-layout row (lg+) */}
                      <CalendarRow
                        post={post}
                        idx={idx}
                        isDone={isDone}
                        isUpdating={isUpdating}
                        canMarkDone={canMarkDone}
                        onToggle={() => handleToggleStatus(post)}
                      />
                      {/* Mobile card row (<lg) */}
                      <div className="lg:hidden">
                        <MobileCardRow
                          post={post}
                          idx={idx}
                          isDone={isDone}
                          isUpdating={isUpdating}
                          canMarkDone={canMarkDone}
                          onToggle={() => handleToggleStatus(post)}
                        />
                      </div>
                    </div>
                  );
                })
              )}

            </div>{/* end .cpe-body */}
          </div>{/* end min-width wrapper */}
        </div>

        {/* ── Export Section ───────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100">
              <i className="ri-file-list-3-line text-gray-600 text-sm" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Export Content Calendar</p>
              <p className="text-xs text-gray-500">Download as CSV and share with your team</p>
              {exportError && (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  {exportError}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <i className={exporting ? 'ri-loader-4-line animate-spin' : 'ri-download-line'} />
              {exporting ? 'Exporting…' : 'Export Calendar'}
            </button>
          </div>
        </div>

      </div>
    </>
  );
}